import { useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { AccountApiError, type AccountConnection } from '@/api'
import { useAccountAppContext } from '@/app/AccountAppContext'

const getConnectionReturnTo = (provider: AccountConnection['provider']) => {
    const url = new URL(window.location.href)
    url.searchParams.delete('connection')
    url.searchParams.delete('provider')
    url.searchParams.set('action', `connect-${provider}`)

    return url.toString()
}

export const useAccountConnections = (connections: AccountConnection[] | undefined) => {
    const { t } = useTranslation()
    const { api, accountKey, ensureFreshAuthentication, reauthenticate, showSuccess, showError } =
        useAccountAppContext()
    const queryClient = useQueryClient()
    const [searchParams, setSearchParams] = useSearchParams()
    const resumedActionRef = useRef<string | null>(null)
    const handledResultRef = useRef<string | null>(null)

    const connect = useMutation({
        mutationFn: async (provider: AccountConnection['provider']) => {
            if (!(await ensureFreshAuthentication(getConnectionReturnTo(provider)))) {
                return null
            }

            return api.startConnection(provider)
        },
        retry: false,
        onSuccess: (result) => {
            if (result) {
                window.location.href = result.authorizationUrl
            }
        },
        onError: (error, provider) => {
            if (error instanceof AccountApiError && error.code === 'reauthenticationRequired') {
                reauthenticate(getConnectionReturnTo(provider))
                return
            }

            showError(error instanceof Error ? error.message : t('security.connections.messages.failed'))
            void queryClient.invalidateQueries({ queryKey: ['account', accountKey, 'security'] })
        },
    })

    const { mutate } = connect

    useEffect(() => {
        const result = searchParams.get('connection')
        const provider = searchParams.get('provider')

        if (!result || (provider !== 'google' && provider !== 'microsoft')) {
            return
        }

        const resultKey = `${provider}:${result}`

        if (handledResultRef.current === resultKey) {
            return
        }

        handledResultRef.current = resultKey
        const providerName = t(`security.connections.providers.${provider}`)

        switch (result) {
            case 'connected':
                showSuccess(t('security.connections.messages.connected', { provider: providerName }))
                break
            case 'alreadyConnected':
                showSuccess(t('security.connections.messages.alreadyConnected', { provider: providerName }))
                break
            case 'providerConflict':
                showError(t('security.connections.messages.providerConflict', { provider: providerName }))
                break
            case 'accountInUse':
                showError(t('security.connections.messages.accountInUse', { provider: providerName }))
                break
            case 'providerUnavailable':
                showError(t('security.connections.messages.providerUnavailable', { provider: providerName }))
                break
            default:
                showError(t('security.connections.messages.failed'))
        }

        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current)
                next.delete('connection')
                next.delete('provider')
                return next
            },
            { replace: true }
        )

        void queryClient.invalidateQueries({ queryKey: ['account', accountKey, 'security'] })
    }, [accountKey, queryClient, searchParams, setSearchParams, showError, showSuccess, t])

    useEffect(() => {
        const action = searchParams.get('action')

        if (!connections || !action || resumedActionRef.current === action) {
            return
        }

        let provider: AccountConnection['provider']

        if (action === 'connect-google') {
            provider = 'google'
        } else if (action === 'connect-microsoft') {
            provider = 'microsoft'
        } else {
            return
        }

        resumedActionRef.current = action

        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current)
                next.delete('action')
                return next
            },
            { replace: true }
        )

        const connection = connections.find((item) => item.provider === provider)
        const providerName = t(`security.connections.providers.${provider}`)

        if (!connection?.enabled) {
            showError(t('security.connections.messages.providerUnavailable', { provider: providerName }))
        } else if (connection.connected) {
            showError(t('security.connections.messages.providerConflict', { provider: providerName }))
        } else {
            mutate(provider)
        }
    }, [connections, mutate, searchParams, setSearchParams, showError, showSuccess, t])

    return connect
}
